import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
    Avatar,
    Box,
    CircularProgress,
    Container,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import RecipeImage from '../components/RecipeImage';
import { RECIPE_TYPES } from '../constants/Constants';
import AuthorFormDialog from '../components/AuthorFormDialog';

const AuthorPage = () => {
    const { author_id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [recipeFilter, setRecipeFilter] = useState('');

    const storedUser = JSON.parse(localStorage.getItem('user')) || {};
    const canEdit = ['editor', 'admin'].includes(storedUser.role);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/authors/${author_id}`, {
            headers: { 'Authorization': 'Bearer ' + Cookies.get('token') },
        })
            .then(r => r.json())
            .then(setData)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [author_id]);

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
            <CircularProgress />
        </Box>
    );

    if (!data?.author) return (
        <Container sx={{ mt: 5 }}>
            <Typography color="text.secondary">Author not found.</Typography>
        </Container>
    );

    const { author, recipes } = data;

    return (
        <Container component="main" sx={{ mt: 5, width: '90%', pb: 12 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' }, gap: 3, mb: 3 }}>
                <Avatar
                    src={author.avatar_url || '/default-author.svg'}
                    alt={author.name}
                    sx={{ width: 120, height: 120, flexShrink: 0 }}
                    imgProps={{ onError: (e) => { e.target.onerror = null; e.target.src = '/default-author.svg'; } }}
                />
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>{author.name}</Typography>
                        {canEdit && (
                            <Tooltip title="Edit author">
                                <IconButton size="small" onClick={() => setEditDialogOpen(true)} sx={{ mb: 1 }}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    {author.bio && (
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                            {author.bio}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, flex: '1 1 auto' }}>
                    Recipes by {author.name}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({recipes.length})
                    </Typography>
                </Typography>
                {recipes.length > 0 && (
                    <TextField
                        size="small"
                        placeholder="Filter recipes…"
                        value={recipeFilter}
                        onChange={e => setRecipeFilter(e.target.value)}
                        sx={{ width: { xs: '100%', sm: 220 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            </Box>

            {recipes.length === 0 ? (
                <Typography color="text.secondary">No recipes yet.</Typography>
            ) : (
                <List disablePadding>
                    {recipes.filter(r => r.title.toLowerCase().includes(recipeFilter.toLowerCase())).map((recipe) => {
                        const type = RECIPE_TYPES.find(t => t.id === recipe.recipe_type_id);
                        return (
                            <ListItem key={recipe.id} disablePadding divider>
                                <ListItemButton component={Link} to={`/recipe/${recipe.id}`} state={{ id: recipe.id }} sx={{ gap: 1.5 }}>
                                    <RecipeImage
                                        loading="lazy"
                                        src={recipe.download_image_url || '/anchovie.jpg'}
                                        alt={recipe.title}
                                        sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0 }}
                                    />
                                    <ListItemText
                                        primary={recipe.title}
                                        secondary={type ? `${type.icon} ${type.title}` : null}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            )}
            {canEdit && (
                <AuthorFormDialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    author={author}
                    onSaved={(saved) => {
                        setData(prev => ({ ...prev, author: saved }));
                        setEditDialogOpen(false);
                    }}
                />
            )}
        </Container>
    );
};

export default AuthorPage;
